import { PlusIcon, XIcon } from 'lucide-react'
import { useState } from 'react'
import { Boton } from '@/components/ui/Boton'
import { Campo, Entrada } from '@/components/ui/Campo'
import { EditorTexto } from '@/components/ui/TextoConFormato'
import { useAvisar } from '@/components/ui/avisador-contexto'
import { ModalMarco, mensajeDeError } from '@/features/admin/ModalMarco'
import { cn } from '@/lib/cn'
import {
  ETIQUETA_ICONO,
  ICONOS_RECURSO,
  TOPE_DESCRIPCION_RECURSO,
  TOPE_ENLACES,
  TOPE_TITULO_RECURSO,
  recursoEntrada,
  type IconoRecurso as Icono,
  type Recurso,
} from '@/lib/schemas/recursos'
import { BotonDriveEnlace } from '@/features/publicacion/ArchivosDeDrive'
import { IconoRecurso } from './IconoRecurso'
import { useGuardarRecurso } from './api'

interface ModalRecursoProps {
  abierto: boolean
  alCerrar: () => void
  proyectoId: string
  /** Null: nuevo. */
  recurso: Recurso | null
  /** Carpeta de Drive del cliente: ahí se abre el selector (Fase 6). */
  carpetaDrive?: string | null
}

interface Valores {
  titulo: string
  icono: Icono
  descripcion: string
  enlaces: { nombre: string; url: string }[]
}

/**
 * C13 · Nuevo / Editar recurso (Fase 5b). Varios enlaces con nombre por
 * recurso (decisión de Serena): la carpeta general, la descarga, cada
 * subcarpeta, como en el correo de entrega de identidad de marca.
 */
export function ModalRecurso(props: ModalRecursoProps) {
  if (!props.abierto) return null
  return <Formulario {...props} />
}

function Formulario({
  alCerrar,
  proyectoId,
  recurso,
  carpetaDrive = null,
}: ModalRecursoProps) {
  const guardar = useGuardarRecurso()
  const avisar = useAvisar()
  const [v, setV] = useState<Valores>(() => ({
    titulo: recurso?.titulo ?? '',
    icono: recurso?.icono ?? 'carpeta',
    descripcion: recurso?.descripcion ?? '',
    enlaces: recurso?.enlaces.length ? recurso.enlaces : [{ nombre: '', url: '' }],
  }))
  const [errores, setErrores] = useState<Record<string, string>>({})
  const [errorGeneral, setErrorGeneral] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)

  const cambiarEnlace = (i: number, parcial: Partial<Valores['enlaces'][number]>) => {
    setV((a) => ({
      ...a,
      enlaces: a.enlaces.map((e, j) => (j === i ? { ...e, ...parcial } : e)),
    }))
  }

  const enviar = () => {
    // Una fila vacía del todo no es un error: se ignora.
    const enlaces = v.enlaces.filter((e) => e.nombre.trim() || e.url.trim())
    const datos = { proyectoId, ...v, enlaces }
    const r = recursoEntrada.safeParse(datos)
    if (!r.success) {
      const porCampo: Record<string, string> = {}
      for (const issue of r.error.issues) {
        const clave =
          issue.path[0] === 'enlaces'
            ? `enlace-${String(issue.path[1])}`
            : String(issue.path[0])
        porCampo[clave] ??= issue.message
      }
      setErrores(porCampo)
      return
    }
    setOcupado(true)
    guardar
      .mutateAsync({ id: recurso?.id ?? null, datos })
      .then(() => {
        avisar(recurso ? 'Recurso guardado' : 'Recurso creado')
        alCerrar()
      })
      .catch((e: unknown) => {
        setErrorGeneral(mensajeDeError(e))
      })
      .finally(() => {
        setOcupado(false)
      })
  }

  return (
    <ModalMarco
      abierto
      alCerrar={alCerrar}
      titulo={recurso ? 'Editar recurso' : 'Nuevo recurso'}
      descripcion="Escribí qué es y pegá los enlaces a donde está. Lo ven todos los proyectos de este cliente."
      guardar="Guardar"
      ocupado={ocupado}
      error={errorGeneral}
      alCambiar={() => {
        setErrores({})
        setErrorGeneral(null)
      }}
      alEnviar={enviar}
    >
      <Campo
        etiqueta="Título"
        error={errores.titulo}
        extremo={`${String(v.titulo.length)} / ${String(TOPE_TITULO_RECURSO)}`}
      >
        {(p) => (
          <Entrada
            id={p.id}
            aria-invalid={p.invalido}
            aria-describedby={p.describedBy}
            value={v.titulo}
            maxLength={TOPE_TITULO_RECURSO}
            autoFocus
            onChange={(e) => {
              setV((a) => ({ ...a, titulo: e.target.value }))
            }}
          />
        )}
      </Campo>

      <fieldset>
        <legend className="mb-2 text-sm font-medium">Ícono</legend>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Ícono">
          {ICONOS_RECURSO.map((i) => (
            <button
              key={i}
              type="button"
              role="radio"
              aria-checked={v.icono === i}
              aria-label={ETIQUETA_ICONO[i]}
              title={ETIQUETA_ICONO[i]}
              onClick={() => {
                setV((a) => ({ ...a, icono: i }))
              }}
              className={cn(
                'flex size-11 items-center justify-center rounded-xl',
                v.icono === i
                  ? 'bg-tinta text-white'
                  : 'border border-borde bg-tarjeta text-tinta-suave hover:bg-lienzo',
              )}
            >
              <IconoRecurso icono={i} className="size-5" />
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-sm font-medium">Enlaces</legend>
        {v.enlaces.map((e, i) => (
          <div key={i} data-enlace-fila={i} className="flex flex-col gap-1">
            <div className="flex items-start gap-2">
              <Entrada
                aria-label={`Nombre del enlace ${String(i + 1)}`}
                placeholder="Nombre (ej.: Manual de marca)"
                className="min-w-0 flex-[2]"
                value={e.nombre}
                maxLength={80}
                onChange={(ev) => {
                  cambiarEnlace(i, { nombre: ev.target.value })
                }}
              />
              <Entrada
                aria-label={`Dirección del enlace ${String(i + 1)}`}
                placeholder="https://…"
                type="url"
                inputMode="url"
                className="min-w-0 flex-[3]"
                value={e.url}
                onChange={(ev) => {
                  cambiarEnlace(i, { url: ev.target.value })
                }}
              />
              <Boton
                type="button"
                variante="sutil"
                tamano="icono"
                aria-label={`Quitar el enlace ${String(i + 1)}`}
                onClick={() => {
                  setV((a) => ({ ...a, enlaces: a.enlaces.filter((_, j) => j !== i) }))
                }}
              >
                <XIcon className="size-4" aria-hidden />
              </Boton>
            </div>
            {errores[`enlace-${String(i)}`] && (
              <p role="alert" className="text-xs text-nuevo">
                {errores[`enlace-${String(i)}`]}
              </p>
            )}
          </div>
        ))}
        {errores.enlaces && (
          <p role="alert" className="text-xs text-nuevo">
            {errores.enlaces}
          </p>
        )}
        <BotonDriveEnlace
          carpetaId={carpetaDrive}
          modo="archivos"
          multiple
          titulo="Elegí archivos o carpetas para este recurso"
          etiqueta="Agregar desde Drive"
          alElegir={(lista) => {
            setV((a) => {
              // La fila vacía del principio se reemplaza con lo elegido.
              const actuales = a.enlaces.filter((e) => e.nombre.trim() || e.url.trim())
              const nuevos = lista
                .filter((d) => !actuales.some((e) => e.url === d.url))
                .map((d) => ({ nombre: d.nombre.slice(0, 80), url: d.url }))
              return { ...a, enlaces: [...actuales, ...nuevos].slice(0, TOPE_ENLACES) }
            })
          }}
        />
        {v.enlaces.length < TOPE_ENLACES && (
          <Boton
            type="button"
            variante="secundario"
            tamano="chico"
            className="self-start"
            onClick={() => {
              setV((a) => ({ ...a, enlaces: [...a.enlaces, { nombre: '', url: '' }] }))
            }}
          >
            <PlusIcon className="size-4" aria-hidden />
            Agregar enlace
          </Boton>
        )}
      </fieldset>

      <Campo
        etiqueta="Descripción"
        ayuda="Opcional."
        error={errores.descripcion}
        extremo={`${String(v.descripcion.length)} / ${String(TOPE_DESCRIPCION_RECURSO)}`}
      >
        {(p) => (
          <EditorTexto
            id={p.id}
            valor={v.descripcion}
            tope={TOPE_DESCRIPCION_RECURSO}
            invalido={p.invalido}
            describedBy={p.describedBy}
            alCambiar={(descripcion) => {
              setV((a) => ({ ...a, descripcion }))
            }}
          />
        )}
      </Campo>
    </ModalMarco>
  )
}
